import React, { InputHTMLAttributes } from "react";
import { Button } from "@core/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@core/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@core/components/ui/command";
import { ChevronsUpDown, Check } from "lucide-react";
import { cn } from "@core/lib/utils";
import {
  FixedSizeList,
  ListChildComponentProps,
} from "react-window";
import { useTranslation } from "react-i18next";

export type LabeledComboBoxProps<T> = InputHTMLAttributes<HTMLInputElement> & {
  items: T[];
  isSelected: (item: T) => boolean;
  onItemToggle: (item: T) => void;
  renderItem: (item: T, selected: boolean) => React.ReactNode;
  getItemValue: (item: T) => string;
  label: string;
  placeholder?: string;
  /** Placeholder for the popover search field (the trigger uses `placeholder`). */
  searchPlaceholder?: string;
  /** Classes for the search row wrapper; use e.g. `px-0` to remove horizontal inset. */
  searchInputWrapperClassName?: string;
  /** Extra classes on the popover search `<input>` (cmdk). */
  searchInputClassName?: string;
  onSearchChange?: (value: string) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  popoverClassName?: string;
  itemSize?: number;
  popoverAlign?: "start" | "center" | "end";
  popoverSide?: "top" | "right" | "bottom" | "left";
  popoverAvoidCollisions?: boolean;
  /** When true, closes the popover after an item is selected. Useful for single-select usage. */
  closeOnSelect?: boolean;
};

export const LabeledComboBox = React.forwardRef<HTMLInputElement, LabeledComboBoxProps<any>>(
  ({
    items,
    isSelected,
    onItemToggle,
    label,
    placeholder,
    searchPlaceholder,
    searchInputWrapperClassName,
    searchInputClassName,
    renderItem,
    getItemValue,
    children,
    className,
    onSearchChange,
    isLoading,
    emptyMessage,
    popoverClassName,
    itemSize = 32,
    popoverAlign = "end",
    popoverSide = "bottom",
    popoverAvoidCollisions = true,
    closeOnSelect = false
  }, ref) => {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const [filteredItems, setFilteredItems] = React.useState(items);

    React.useEffect(() => {
      setFilteredItems(items);
    }, [items]);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase();
      if (onSearchChange) {
        onSearchChange(searchValue);
      } else {
        const filtered = items.filter((item) =>
          getItemValue(item).toLowerCase().includes(searchValue)
        );
        setFilteredItems(filtered);
      }
    };

    function rowRenderer({ index, style, data }: ListChildComponentProps) {
      const item = data.items[index];
      const selected = data.isSelected(item);

      return (
        <div style={style}>
          <CommandItem
            key={item.Id ?? item.Key}
            value={data.getItemValue(item)}
            onSelect={() => {
              data.onItemToggle(item);
              if (data.closeOnSelect) {
                setOpen(false);
              }
            }}
          >
            <Check
              className={cn("mr-2 h-4 w-4 transition-opacity duration-150", {
                "opacity-0": !selected,
                "opacity-100": selected,
              })}
            />
            {data.renderItem(item, selected)}
          </CommandItem>
        </div>
      );
    }

    return (
      <div className={className}>
        {label && (
          <label htmlFor="generic-combobox">
            <p className="mb-1">{label}</p>
          </label>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              type="button"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between rounded-[var(--form-radius)]"
            >
              <span>{placeholder || t("ui.selectEllipses", "Select...")}</span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className={cn("w-[28rem] p-0 z-[100]", popoverClassName)}
            align={popoverAlign}
            side={popoverSide}
            avoidCollisions={popoverAvoidCollisions}>
            <Command shouldFilter={false} key={open ? "open" : "closed"}>
              <CommandInput
                id="generic-combobox"
                placeholder={
                  (searchPlaceholder ?? t("ui.searchEllipses", "Search..."))?.trim() ||
                  "Search..."
                }
                wrapperClassName={searchInputWrapperClassName}
                className={searchInputClassName}
                onInput={handleInput}
                ref={ref}
              />
              <CommandList className="overflow-y-hidden">
                {isLoading ? (
                  <div className="text-sm text-center p-2">{t("ui.loadingEllipses", "Loading...")}</div>
                ) : (
                  <>
                    <CommandEmpty>{emptyMessage ?? t("ui.noItemsFound", "No items found.")}</CommandEmpty>
                    <CommandGroup>
                      <FixedSizeList
                        height={300}
                        width="100%"
                        itemSize={itemSize}
                        itemCount={filteredItems.length}
                        itemData={{
                          items: filteredItems,
                          isSelected,
                          onItemToggle,
                          getItemValue,
                          renderItem,
                          closeOnSelect,
                        }}
                      >
                        {rowRenderer}
                      </FixedSizeList>
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {/* Render any children passed to the component */}
        {children}
      </div>
    );
  }
);

LabeledComboBox.displayName = "LabeledComboBox";
